<template>
  <!-- Caller supplies absolute inset-0; avoid relative/size-full (collapses on min-h-screen). -->
  <div class="overflow-hidden">
    <div v-if="isFirefox" class="absolute inset-0 overflow-hidden">
      <div class="absolute inset-0" :style="firefoxGradientStyle" />
    </div>

    <svg
      v-else
      class="absolute inset-0 size-full"
      width="1920"
      height="1080"
      :viewBox="`${dx} ${dy} ${1920 + dx} ${1080 + dy}`"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity="0.6" filter="url(#filter0_f_428_30111)">
        <path
          d="M849.784 145.077C817.875 23.6665 890.504 -100.604 1012.01 -132.49C1133.51 -164.375 1257.87 -91.8001 1289.78 29.6107L1373.55 348.338C1405.46 469.749 1332.83 594.02 1211.33 625.905C1089.83 657.791 965.461 585.216 933.552 463.805L849.784 145.077Z"
          :fill="gradientColorSchemes[colorscheme].color1"
        />
        <path
          d="M1388.63 600.813C1352.27 468.608 1430.96 332.179 1563.67 297.353C1695.31 262.805 1830.06 341.439 1864.63 472.986L1911.95 653.015C1932.19 730.047 1913.74 812.105 1862.45 873.071C1735.23 1024.31 1490.41 970.791 1438.01 780.289L1388.63 600.813Z"
          :fill="gradientColorSchemes[colorscheme].color2"
        />
        <path
          d="M-71.8097 715.607C-106.582 583.303 -27.4362 447.883 104.967 413.137L216.891 383.765C349.294 349.019 484.816 428.105 519.588 560.409L559.793 713.383C594.565 845.686 515.419 981.107 383.016 1015.85L271.092 1045.22C138.689 1079.97 3.1671 1000.88 -31.6049 868.581L-71.8097 715.607Z"
          :fill="gradientColorSchemes[colorscheme].color3"
        />
        <path
          d="M757.803 1176.78C716.105 1140.06 984.822 776.499 1066.43 683.974L1092.94 653.913C1174.54 561.387 1274.5 516.149 1316.2 552.871L1323.47 559.277C1365.17 595.998 1332.82 700.774 1251.22 793.299L1224.7 823.36C1143.1 915.886 798.089 1243.87 756.391 1207.15L757.803 1176.78Z"
          :fill="gradientColorSchemes[colorscheme].color4"
        />
        <path
          d="M505.658 359.646C478.583 256.63 540.208 151.187 643.302 124.133C746.395 97.0788 851.917 158.658 878.992 261.674L966.478 594.547C993.552 697.563 931.927 803.006 828.834 830.06C725.74 857.114 620.218 795.535 593.143 692.519L505.658 359.646Z"
          :fill="gradientColorSchemes[colorscheme].color5"
        />
      </g>
      <defs>
        <filter
          id="filter0_f_428_30111"
          x="-330"
          y="-390"
          width="2500"
          height="1850"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="100"
            result="effect1_foregroundBlur_428_30111"
          />
        </filter>
      </defs>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { GradientColorScheme, gradientColorSchemes } from "@/types";

const props = withDefaults(
  defineProps<{
    colorscheme?: GradientColorScheme;
    dx?: number;
    dy?: number;
  }>(),
  {
    colorscheme: "networks",
    dx: 0,
    dy: 0,
  },
);

const isFirefox = ref(false);

onMounted(() => {
  isFirefox.value = navigator.userAgent.toLowerCase().includes("firefox");
});

const firefoxGradientStyle = computed(() => {
  const colors = gradientColorSchemes[props.colorscheme];

  const xOffset = (props.dx / 1920) * 100;
  const yOffset = (props.dy / 1080) * 100;
  return {
    background: `
      radial-gradient(ellipse 800px 1000px at ${57.3 - xOffset}% ${23.1 - yOffset}%, ${colors.color1}ee, transparent 60%),
      radial-gradient(ellipse 700px 900px at ${87 - xOffset}% ${56.7 - yOffset}%, ${colors.color2}dd, transparent 60%),
      radial-gradient(ellipse 750px 800px at ${12.7 - xOffset}% ${67.5 - yOffset}%, ${colors.color3}ee, transparent 60%),
      radial-gradient(ellipse 650px 850px at ${54.2 - xOffset}% ${80.4 - yOffset}%, ${colors.color4}bb, transparent 60%),
      radial-gradient(ellipse 700px 800px at ${36 - xOffset}% ${32.8 - yOffset}%, ${colors.color5}dd, transparent 60%)
    `,
    filter: "blur(100px)",
    opacity: "0.75",
    transform: "translateZ(0)",
    willChange: "transform",
  };
});
</script>
